<template>
	<div class="o-carSingle__step1">
		
		<!-- Loader overlay during initial boot -->
		<div
			v-if="!store.isReady"
			class="o-carSingle__loaderOverlay"
			role="status"
			aria-live="polite"
			aria-busy="true"
		>
			<span class="u-visuallyHidden">Bezig met laden…</span>
			<Loader />
		</div>

		<div class="o-carSingle__formStepContent" :class="{ 'u-visuallyHidden': !store.isReady }">

			<div
				class="o-carSingle__formBlocks -isZakelijk"
				ref="formBlocks"
			>
				<div v-if="!isPriceTooLow" class="o-carSingle__formBlock u-shadow">

					<div class="o-carSingle__formBlockHeader">
						<div class="o-carSingle__formBlockHeaderMain">
							<h4 v-if="headerCopy.title">{{ headerCopy.title }}</h4>
							<Usp :label="headerCopy.usp" :icon="false" />
						</div>
						<div class="o-carSingle__formBlockHeaderPrice">
							<div class="o-carSingle__infoLabel">
								<span>Aanschafprijs</span>
								<InfoPopup label="Aanschafprijs" align="left">
									<p>{{ infoCopy.aankoopbedrag }}</p>
								</InfoPopup>
							</div>
							<div
								class="u-priceUpdating"
								:class="{ '-isUpdating': store.isAankoopbedragUpdating }"
							>
								<template v-if="store.isAankoopbedragUpdating">
									<span class="u-colorGray"><Loader size="small" /></span>
								</template>
								<template v-else>
									<strong>{{ formatCurrency( store.aankoopbedrag ) }}</strong>
									<span
										v-if="store.btwStatus"
										class="u-fs14 u-colorGray u-mlSmall"
									>
										{{ store.btwStatus === 'Marge' ? ' marge' : ( store.btwStatus === 'in' ? ' incl. btw' : ' excl. btw' ) }}
									</span>
								</template>
							</div>
						</div>
					</div>

					<div class="o-carSingle__formField u-topSpace">
						<RadioSegment
							:key="`term-months-${store.type}`"
							v-model="termMonths"
							name="term-months"
							label="Looptijd in maanden"
							:options="termOptions"
						/>
					</div>

					<div class="o-carSingle__splitField u-topSpace">
						<div>
							<div class="o-carSingle__infoLabel">
								<label for="downpayment">
									<span>Aanbetaling</span>
									<span class="u-fs14 -splitLabel" v-if="maxDownpayment > 0"> (max {{ formatCurrency( maxDownpayment ) }})</span>
								</label>
								<InfoPopup label="Aanbetaling">
									<p>{{ infoCopy.aanbetaling }}</p>
								</InfoPopup>
							</div>
							<input
								class="u-textInput"
								id="downpayment"
								type="number"
								inputmode="numeric"
								step="1"
								min="0"
								:max="maxDownpayment"
								v-model="downpayment"
								ref="downpaymentInput"
								:aria-invalid="false"
								@blur="handleDownpaymentBlur"
							/>
						</div>
						<div>
							<div class="o-carSingle__infoLabel">
								<label for="final-term">
									<span>Slottermijn</span>
									<span class="u-fs14 -splitLabel" v-if="maxSlottermijn > 0"> (max {{ formatCurrency( maxSlottermijn ) }})</span>
								</label>
								<InfoPopup label="Slottermijn">
									<p>{{ infoCopy.slottermijn }}</p>
								</InfoPopup>
							</div>
							<input
								class="u-textInput"
								id="final-term"
								type="number"
								inputmode="numeric"
								step="1"
								min="0"
								:max="maxSlottermijn"
								v-model="finalTerm"
								ref="finalTermInput"
								:aria-invalid="false"
								@blur="handleFinalTermBlur"
							/>
						</div>
					</div>

					<div
						v-if="!store.isStep1Valid && Object.keys( store.step1ValidationErrors ).length"
						class="u-error u-topSpace"
						role="status"
						aria-live="polite"
						tabindex="-1"
						ref="leaseErrorSummary"
					>
						<ul class="u-listReset">
							<li v-for="(msg, key) in store.step1ValidationErrors" :key="key">{{ msg }}</li>
						</ul>
					</div>

					<hr class="u-bottomSpace u-topSpace">

					<div class="">
						<InfoCallout
							:label="inruilenCopy.buttonTitle"
							:block-title="inruilenCopy.blockTitle"
							:block-content="inruilenCopy.blockContent"
						/>
					</div>

					<div class="o-carSingle__formField -row u-topSpace">
						<span class="o-carSingle__infoLabel">
							<span>Maandbedrag</span>
							<InfoPopup label="Maandbedrag">
								<p>{{ infoCopy.maandbedrag }}</p>
							</InfoPopup>
						</span>
					<div
						class="u-priceUpdating"
						:class="{ '-isUpdating': store.isPriceUpdating }"
					>
						<template v-if="store.isPriceUpdating">
							<span class="u-colorGray"><Loader size="small" /></span>
						</template>
						<template v-else>
							<strong>{{ store.formattedMaandbedrag }}</strong>
							<span class="u-colorGray"> p/m</span>
						</template>
					</div>
					</div>

					<div class="u-topSpace">
						<button
							type="button"
							:class="leaseButtonVariant"
							class="-fullWidth"
							:disabled="leaseButtonDisabled"
							:aria-disabled="leaseButtonDisabled ? 'true' : 'false'"
							@click="handleLeaseClick"
						>
							{{ leaseButtonLabel }}
						</button>
					</div>
					
					<span class="u-fs15 u-microCopy">{{ leaseText }}</span>

				</div>

				<!-- Geen lease mogelijk: toon alleen de aanschafprijs (koop-aanvraag is verwijderd). -->
				<div v-else class="o-carSingle__formBlock u-shadow">

					<h4 v-if="koopHeaderTitle">{{ koopHeaderTitle }}</h4>

					<div class="o-carSingle__formField -row u-topSpace">
						<div class="o-carSingle__infoLabel">
							<span>Aanschafprijs</span>
							<InfoPopup label="Aanschafprijs">
								<p>{{ infoCopy.aankoopbedrag }}</p>
							</InfoPopup>
						</div>
					<div
						class="u-priceUpdating"
						:class="{ '-isUpdating': store.isAankoopbedragUpdating }"
					>
						<template v-if="store.isAankoopbedragUpdating">
							<span class="u-colorGray"><Loader size="small" /></span>
						</template>
						<template v-else>
							<strong>{{ formatCurrency( store.aankoopbedrag ) }}</strong>
							<span
								v-if="store.btwStatus"
								class="u-fs14 u-colorGray u-mlSmall"
							>
								{{ store.btwStatus === 'Marge' ? ' marge' : ( store.btwStatus === 'in' ? ' incl. btw' : ' excl. btw' ) }}
							</span>
						</template>
					</div>
					</div>

					<div v-if="!store.isPriceUpdating && store.btwStatus === 'in' && store.btwBedrag" class="u-topSpace">
						<span
							class="u-fs14 u-colorGray u-priceUpdating"
						>
							Waarvan {{ formatCurrency( store.btwBedrag ) }} BTW
						</span>
					</div>

				</div>

			</div>
		</div>

		<teleport to="#car-sticky-root">
			<StickyCar
				v-if="store.isReady"
				:sticky-visible="isStickyVisible"
			/>
		</teleport>

	</div>
</template>

<script setup>

import { ref, computed, watch, onMounted, nextTick, onBeforeUnmount, inject } from 'vue';
import { useRouter } from 'vue-router';
import { useFormStore } from '../stores/form.js';
import InfoPopup from '../components/InfoPopup.vue';
import RadioSegment from '../components/RadioSegment.vue';
import Loader from '../components/Loader.vue';
import Usp from '../components/Usp.vue';
import InfoCallout from '../components/InfoCallout.vue';
import StickyCar from '../components/StickyCar.vue';
import { getDefaultAanbetaling, getDefaultSlottermijn72, getSlottermijnPercentage, getDefaultSlottermijnForType } from '../utils/defaults.js';
import { getInfoCopy, getInruilenCopy } from '../content/info.js';
import { trackRequestStarted } from '@/js/gtm-tracking.js';

const router = useRouter();
const store = useFormStore();

// Free-text number inputs (aanbetaling, slottermijn) fire their watcher on every
// keystroke. Debounce the server round-trip so typing "10000" results in one
// calculate() call instead of five. Discrete controls (looptijd, inruilen, type)
// keep calling store.calculate() directly since they emit a single event.
const CALCULATE_DEBOUNCE_MS = 300;
let calculateTimer = null;

const debouncedCalculate = () => {
	if ( calculateTimer ) {
		clearTimeout( calculateTimer );
	}
	// Show the price as updating immediately, even though the call is deferred.
	store.isPriceUpdating = true;
	calculateTimer = setTimeout( () => {
		calculateTimer = null;
		store.calculate();
	}, CALCULATE_DEBOUNCE_MS );
};

onBeforeUnmount( () => {
	if ( calculateTimer ) {
		clearTimeout( calculateTimer );
		calculateTimer = null;
	}
} );

const copyConfig = inject( 'copyConfig', {} );

const infoCopy = computed( () => getInfoCopy( copyConfig ) );
const inruilenCopy = computed( () => getInruilenCopy( copyConfig ) );

// Term opties: zakelijk tot 72 maanden
const termOptions = computed( () => [ 12, 24, 36, 48, 60, 72 ] );

const headerCopy = computed( () => {
	const uspKey = 'api_zakelijk_lease_usp';
	const titleKey = 'api_zakelijk_lease_titel';

	const uspFromConfig =
		copyConfig && typeof copyConfig === 'object'
			? ( copyConfig[uspKey] || '' )
			: '';

	const titleFromConfig =
		copyConfig && typeof copyConfig === 'object'
			? ( copyConfig[titleKey] || '' )
			: '';

	return {
		title: titleFromConfig,
		usp: uspFromConfig,
	};
} );

const termMonths = computed( {
	get: () => store.looptijd,
	set: ( value ) => {
		store.updateCalculation( { looptijd: value } );
		// calculate() wordt aangeroepen door de watcher op store.looptijd
	},
} );

const downpayment = ref( store.aanbetaling !== null ? store.aanbetaling : 0 );
const finalTerm = ref( store.slottermijn !== null ? store.slottermijn : 0 );
const downpaymentInput = ref( null );
const finalTermInput = ref( null );
const leaseErrorSummary = ref( null );
const pendingEditScroll = ref( false );
const formBlocks = ref( null );
const isStickyVisible = ref( false );

const leaseButtonDisabled = computed( () => ! store.isReady || ! store.isStep1Valid );

const leaseButtonLabel = computed( () => {
	const key = 'api_zakelijk_lease_button';
	if ( ! copyConfig || typeof copyConfig !== 'object' ) {
		return 'Vrijblijvend leasevoorstel aanvragen';
	}
	return copyConfig[key] || 'Vrijblijvend leasevoorstel aanvragen';
} );

const leaseButtonVariant = computed( () => 'a-button -primary' );

const leaseText = computed( () => {
	const key = 'api_zakelijk_lease_microcopy';
	if ( ! copyConfig || typeof copyConfig !== 'object' ) {
		return '';
	}
	return copyConfig[key] || '';
} );

const koopHeaderTitle = computed( () => {
	const titleKey = 'api_zakelijk_koop_titel';
	if ( ! copyConfig || typeof copyConfig !== 'object' ) {
		return '';
	}
	return copyConfig[titleKey] || '';
} );

const isPriceTooLow = computed( () => {
	// Verberg de lease-calculator alleen als er geen lease-data beschikbaar is.
	return ! store.hasLeaseData;
} );

const maxDownpayment = computed( () => {
	const aankoopbedrag = store.aankoopbedrag || 0;
	return Math.max( 0, aankoopbedrag - 4000 );
} );

// Computed property voor resterend financieringsbedrag
const resterendBedrag = computed( () => {
	const aankoopbedrag = store.aankoopbedrag || 0;
	const aanbetaling = store.aanbetaling || 0;
	return Math.max( 0, aankoopbedrag - aanbetaling );
} );

// Computed property voor maximale slottermijn
const maxSlottermijn = computed( () => {
	const looptijd = store.looptijd || 72;
	const percentage = getSlottermijnPercentage( looptijd, store.type );
	return Math.round( resterendBedrag.value * percentage );
} );

const formatCurrency = ( value ) => {
	if ( value === null || value === undefined ) {
		return '';
	}
	const numValue = Number( value );
	const isWholeNumber = Number.isInteger( numValue );
	return new Intl.NumberFormat( 'nl-NL', {
		style: 'currency',
		currency: 'EUR',
		minimumFractionDigits: isWholeNumber ? 0 : 2,
		maximumFractionDigits: isWholeNumber ? 0 : 2,
	} ).format( numValue );
};

const handleDownpaymentBlur = () => {
	// Handle empty value (null, empty string, or whitespace) for number input
	const value = downpayment.value;
	if ( value === null || value === '' || ( typeof value === 'string' && value.trim() === '' ) ) {
		downpayment.value = 0;
		store.updateCalculation( { aanbetaling: 0 } );
		debouncedCalculate();
	}
};

const handleFinalTermBlur = () => {
	// Handle empty value (null, empty string, or whitespace) for number input
	const value = finalTerm.value;
	if ( value === null || value === '' || ( typeof value === 'string' && value.trim() === '' ) ) {
		finalTerm.value = 0;
		store.updateCalculation( { slottermijn: 0 } );
		debouncedCalculate();
	}
};

const updateZakelijkTableVisibility = () => {
	const zakelijkTabel = document.querySelector( '.js-zakelijkTabel' );
	if ( zakelijkTabel ) {
		zakelijkTabel.classList.add( '-visible' );
	}
};

const updateZakelijkPrijsblokkenVisibility = () => {
	const zakelijkPrijsblokken = document.querySelector( '.js-zakelijkPrijsblokken' );
	if ( zakelijkPrijsblokken ) {
		zakelijkPrijsblokken.classList.add( '-visible' );
	}
};

const updateZakelijkTextVisibility = () => {
	const zakelijkText = document.querySelector( '.js-zakelijkText' );
	if ( zakelijkText ) {
		zakelijkText.classList.add( '-visible' );
	}
};

const handleStickyLeaseInvalid = async () => {
	if ( ! store.isStep1Valid ) {
		await focusFirstLeaseError();
	}
};

const performEditScroll = () => {
	if ( ! downpaymentInput.value ) {
		return;
	}

	const element = downpaymentInput.value;

	element.focus();

	const rect = element.getBoundingClientRect();
	const viewportHeight = window.innerHeight || document.documentElement.clientHeight || 0;
	const currentScroll = window.pageYOffset || document.documentElement.scrollTop || 0;

	const targetScroll = rect.bottom + currentScroll - ( viewportHeight * 0.3 );

	window.scrollTo( {
		top: targetScroll,
		behavior: 'smooth',
	} );
};

const handleEditPriceClick = async () => {
	if ( ! store.isReady || store.isPriceUpdating ) {
		pendingEditScroll.value = true;
		return;
	}

	await nextTick();
	performEditScroll();
};

watch(
	() => ( {
		isReady: store.isReady,
		isPriceUpdating: store.isPriceUpdating,
	} ),
	async ( state ) => {
		if ( pendingEditScroll.value && state.isReady && ! state.isPriceUpdating ) {
			pendingEditScroll.value = false;
			await nextTick();
			performEditScroll();
		}
	}
);

onMounted( () => {
	document.addEventListener( 'greven:stickyLeaseClickInvalid', handleStickyLeaseInvalid );
	document.addEventListener( 'greven:editPriceClick', handleEditPriceClick );
} );

onBeforeUnmount( () => {
	document.removeEventListener( 'greven:stickyLeaseClickInvalid', handleStickyLeaseInvalid );
	document.removeEventListener( 'greven:editPriceClick', handleEditPriceClick );
} );

// Sync store values to local refs on mount
		onMounted( async () => {
		// Fetch car data (placeholder-capable) so we have aankoopbedrag available
		await store.fetchCarData();
		store.updateCalculation( { type: 0 } );
		
		// Valideer en synchroniseer looptijd
		const currentLooptijd = store.looptijd;
		const availableOptions = termOptions.value;
		if ( currentLooptijd && ! availableOptions.includes( currentLooptijd ) ) {
			// Als huidige looptijd niet beschikbaar is voor huidig type, pas aan naar maximum beschikbare looptijd
			const maxLooptijd = Math.max( ...availableOptions );
			store.updateCalculation( { looptijd: maxLooptijd } );
		} else if ( currentLooptijd && availableOptions.includes( currentLooptijd ) ) {
			// Als looptijd wel beschikbaar is, expliciet synchroniseren om ervoor te zorgen dat RadioSegment de juiste waarde toont
			store.updateCalculation( { looptijd: currentLooptijd } );
		}
		// Set default aanbetaling to €0 if not set
		if ( store.aanbetaling === null ) {
				const defaultAanbetaling = getDefaultAanbetaling( store.aankoopbedrag );
			downpayment.value = defaultAanbetaling;
			store.updateCalculation( { aanbetaling: defaultAanbetaling } );
		} else {
			downpayment.value = store.aanbetaling;
		}
		// Set default slottermijn
		if ( store.slottermijn === null ) {
			const defaultSlottermijn = getDefaultSlottermijnForType(
				resterendBedrag.value,
				store.looptijd || 72,
				0
			);
			finalTerm.value = defaultSlottermijn;
			store.updateCalculation( { slottermijn: defaultSlottermijn } );
		} else {
			finalTerm.value = store.slottermijn;
		}
		
		// Initial calculation (await to finish before showing UI). Cancel any
		// debounce scheduled by watchers that fired while syncing values above,
		// so the awaited call below is the only initial round-trip.
		if ( calculateTimer ) {
			clearTimeout( calculateTimer );
			calculateTimer = null;
		}
		await store.calculate();
		// Mark app as ready - show form content
		store.isReady = true;
		updateZakelijkTableVisibility();
		updateZakelijkPrijsblokkenVisibility();
		updateZakelijkTextVisibility();
	} );

	// Watch number inputs and sync to store
	watch( downpayment, ( newValue ) => {
		// Handle number input: value can be number, null, or empty string
		// Allow empty field during typing - don't reset to '0' automatically
		// Reset to '0' only happens on blur (handleDownpaymentBlur)
		if ( newValue === null || newValue === '' || ( typeof newValue === 'string' && newValue.trim() === '' ) ) {
			return;
		}
		
		// Convert to number if it's a string, otherwise use as-is
		const numValue = typeof newValue === 'string' ? parseFloat( newValue.trim() ) : Number( newValue );
		if ( ! isNaN( numValue ) && numValue !== null ) {
			// Auto-correctie: als waarde hoger is dan maximum, corrigeer naar maximum
			if ( numValue > maxDownpayment.value ) {
				const corrected = maxDownpayment.value;
				downpayment.value = corrected;
				store.updateCalculation( { aanbetaling: corrected } );
				debouncedCalculate();
				return;
			}
			// Store the numeric value
			store.updateCalculation( { aanbetaling: numValue } );
			debouncedCalculate();
		}
	} );

	watch( finalTerm, ( newValue ) => {
		// Handle number input: value can be number, null, or empty string
		// Allow empty field during typing - don't reset to '0' automatically
		// Reset to '0' only happens on blur (handleFinalTermBlur)
		if ( newValue === null || newValue === '' || ( typeof newValue === 'string' && newValue.trim() === '' ) ) {
			return;
		}
		
		// Convert to number if it's a string, otherwise use as-is
		const numValue = typeof newValue === 'string' ? parseFloat( newValue.trim() ) : Number( newValue );
		if ( ! isNaN( numValue ) && numValue !== null ) {
			// Auto-correctie: als waarde hoger is dan maximum voor huidige looptijd, corrigeer naar maximum
			if ( numValue > maxSlottermijn.value ) {
				const corrected = maxSlottermijn.value;
				finalTerm.value = corrected;
				store.updateCalculation( { slottermijn: corrected } );
				debouncedCalculate();
				return;
			}
			// Store the numeric value
			store.updateCalculation( { slottermijn: numValue } );
			debouncedCalculate();
		}
	} );

	// Recalculate on changes to looptijd, inruilen, and type
	watch( () => store.looptijd, () => {
		// Check of huidige slottermijn nog geldig is voor nieuwe looptijd
		const currentSlottermijn = store.slottermijn;
		if ( currentSlottermijn !== null && currentSlottermijn > maxSlottermijn.value ) {
			// Alleen corrigeren als huidige waarde boven nieuw maximum komt
			const corrected = maxSlottermijn.value;
			finalTerm.value = corrected;
			store.updateCalculation( { slottermijn: corrected } );
			debouncedCalculate();
		} else {
			// Anders alleen recalculation triggeren, waarde blijft hetzelfde
			debouncedCalculate();
		}
	} );
	watch( () => store.inruilen, () => {
		debouncedCalculate();
	} );
	
	// Watch termOptions om te controleren of store.looptijd nog steeds beschikbaar is
	watch( termOptions, ( newOptions ) => {
		const currentLooptijd = store.looptijd;
		if ( currentLooptijd && ! newOptions.includes( currentLooptijd ) ) {
			// Als huidige looptijd niet meer beschikbaar is, pas aan naar maximum beschikbare looptijd
			const maxLooptijd = Math.max( ...newOptions );
			store.updateCalculation( { looptijd: maxLooptijd } );
		}
	}, { immediate: false } );
	

const focusFirstLeaseError = async () => {
	await nextTick();
	// Aanbetaling check verwijderd - geen error meer voor aanbetaling (auto-correctie wordt gebruikt)
	// Slottermijn check verwijderd - geen error meer voor slottermijn (auto-correctie wordt gebruikt)
	if ( leaseErrorSummary.value ) {
		leaseErrorSummary.value.focus();
	}
};

const proceedToStep2 = () => {
	store.markStepCompleted( 1 );
	
	// Track request started event for GTM
	const requestType = store.submissionMode === 'koop' ? 'buy' : 'lease';
	const pageType = 'zakelijk';
	trackRequestStarted( requestType, {
		car_id: store.carId,
		submission_mode: store.submissionMode,
		pagetype: pageType,
	} );
	
	router.push( '/step/2' );
};

const handleLeaseClick = async () => {
	store.setSubmissionMode( 'lease' );
	if ( ! store.isStep1Valid ) {
		await focusFirstLeaseError();
		return;
	}
	proceedToStep2();
};

const VIEWPORT_TRIGGER_RATIO = 0.3;
let stickyRafId = null;

const evaluateStickyVisibility = () => {
	stickyRafId = null;

	if ( ! formBlocks.value ) {
		isStickyVisible.value = false;
		return;
	}

	const rect = formBlocks.value.getBoundingClientRect();
	const viewportHeight = window.innerHeight || document.documentElement.clientHeight || 0;
	const triggerY = viewportHeight * VIEWPORT_TRIGGER_RATIO;

	const scrollTop = window.pageYOffset || document.documentElement.scrollTop || 0;
	const documentElement = document.documentElement || document.body;
	const documentHeight = documentElement.scrollHeight || 0;
	const nearBottomBuffer = viewportHeight * 0.2;

	const isNearBottom = ( scrollTop + viewportHeight ) >= ( documentHeight - nearBottomBuffer );

	// Visible once the bottom of the form blocks passes the trigger line (scrolling down),
	// and hidden again when scrolling back up past that same line.
	const shouldBeVisible = rect.bottom <= triggerY && ! isNearBottom;

	isStickyVisible.value = shouldBeVisible;
};

const handleStickyScrollOrResize = () => {
	if ( stickyRafId === null ) {
		stickyRafId = window.requestAnimationFrame( evaluateStickyVisibility );
	}
};

onMounted( () => {
	window.addEventListener( 'scroll', handleStickyScrollOrResize, { passive: true } );
	window.addEventListener( 'resize', handleStickyScrollOrResize );

	nextTick( () => {
		handleStickyScrollOrResize();
	} );
} );

onBeforeUnmount( () => {
	window.removeEventListener( 'scroll', handleStickyScrollOrResize );
	window.removeEventListener( 'resize', handleStickyScrollOrResize );

	if ( stickyRafId !== null ) {
		window.cancelAnimationFrame( stickyRafId );
		stickyRafId = null;
	}
} );

</script>